/**
 * Page Liste des Salariés - ÉTAPE B + C
 * KPIs, recherche instantanée (debounce 300ms), filtres, tableau paginé
 * + Modal de création salarié
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useAuth } from "../../App";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Skeleton } from "../../components/ui/skeleton";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { 
  Users, Search, Plus, ArrowUp, ArrowDown, ChevronLeft, ChevronRight, ChevronRight as ChevronRightIcon,
  Briefcase, UserPlus, AlertCircle, RefreshCw, X
} from "lucide-react";
import CreateEmployeeModal from "../../components/rh/CreateEmployeeModal";
import { formatDateFr } from "../../components/ui/date-picker-fr";
import { 
  getContractTypeLabel, 
  getEmployeeStatusLabel,
  CONTRACT_TYPE_OPTIONS,
  EMPLOYEE_STATUS_OPTIONS
} from "../../lib/hr-constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Debounce hook
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    
    return () => clearTimeout(handler);
  }, [value, delay]);
  
  return debouncedValue;
}

// Contract type badge colors
const CONTRACT_BADGE_COLORS = {
  cdi: "bg-green-100 text-green-700 border-green-200",
  cdd: "bg-orange-100 text-orange-700 border-orange-200",
  alternance: "bg-blue-100 text-blue-700 border-blue-200",
  stage: "bg-purple-100 text-purple-700 border-purple-200",
  interim: "bg-slate-100 text-slate-700 border-slate-200",
};

// Status badge colors
const STATUS_BADGE_COLORS = {
  active: "bg-emerald-100 text-emerald-700",
  on_leave: "bg-amber-100 text-amber-700",
  terminated: "bg-slate-100 text-slate-500",
};

// Status labels
const STATUS_LABELS = {
  active: "Actif",
  on_leave: "En congé",
  terminated: "Sorti",
};

export default function SalariesList() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // State
  const [employees, setEmployees] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  
  // Filters
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "active");
  const [contractFilter, setContractFilter] = useState(searchParams.get("contract") || "all");
  
  // Pagination
  const [page, setPage] = useState(parseInt(searchParams.get("page")) || 1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;
  
  // Sorting
  const [sortBy, setSortBy] = useState(searchParams.get("sort") || "last_name");
  const [sortOrder, setSortOrder] = useState(searchParams.get("order") || "asc");
  
  // Debounced search
  const debouncedSearch = useDebounce(search, 300);
  
  // Count active filters
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (statusFilter && statusFilter !== "active") count++;
    if (contractFilter && contractFilter !== "all") count++;
    if (debouncedSearch) count++;
    return count;
  }, [statusFilter, contractFilter, debouncedSearch]);
  
  // Fetch employees
  const fetchEmployees = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        sort: sortBy,
        order: sortOrder,
      });
      
      if (debouncedSearch) {
        params.append("search", debouncedSearch);
      }
      if (statusFilter && statusFilter !== "all") {
        params.append("status", statusFilter);
      }
      if (contractFilter && contractFilter !== "all") {
        params.append("contract_type", contractFilter);
      }
      
      const [empRes, statsRes] = await Promise.all([
        axios.get(`${API}/hr/employees?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/hr/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setEmployees(empRes.data.data || []);
      setTotal(empRes.data.total || 0);
      setTotalPages(empRes.data.pages || 1);
      setStats(statsRes.data);
      
    } catch (err) {
      console.error("Error fetching employees:", err);
      setError("Impossible de charger les salariés. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  }, [token, page, limit, sortBy, sortOrder, debouncedSearch, statusFilter, contractFilter]);
  
  // Update URL params
  useEffect(() => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (statusFilter !== "active") params.set("status", statusFilter);
    if (contractFilter !== "all") params.set("contract", contractFilter);
    if (page > 1) params.set("page", page.toString());
    if (sortBy !== "last_name") params.set("sort", sortBy);
    if (sortOrder !== "asc") params.set("order", sortOrder);
    setSearchParams(params, { replace: true });
  }, [search, statusFilter, contractFilter, page, sortBy, sortOrder, setSearchParams]);
  
  // Fetch on mount and filter change
  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);
  
  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter, contractFilter]);
  
  // Handle sort
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };
  
  // Clear all filters
  const clearFilters = () => {
    setSearch("");
    setStatusFilter("active");
    setContractFilter("all");
    setPage(1);
  };
  
  // Navigate to employee detail
  const handleRowClick = (employeeId) => {
    navigate(`/rh/salaries/${employeeId}`);
  };
  
  // Handle employee created
  const handleEmployeeCreated = (employee, createAnother) => {
    fetchEmployees();
    if (!createAnother) {
      navigate(`/rh/salaries/${employee.id}`);
    }
  };
  
  // KPIs computed from stats
  const kpis = useMemo(() => {
    if (!stats) return null;
    return {
      effectif: stats.effectif_total || 0,
      cdi: stats.by_contract_type?.cdi || 0,
      cdd: stats.by_contract_type?.cdd || 0,
      alternance: stats.by_contract_type?.alternance || 0,
      arrivals: stats.movements?.arrivals_30d || 0,
    };
  }, [stats]);
  
  // Sort icon component
  const SortIcon = ({ field }) => {
    if (sortBy !== field) {
      return <ArrowUp className="w-3.5 h-3.5 text-slate-300" />;
    }
    return sortOrder === "asc" 
      ? <ArrowUp className="w-3.5 h-3.5 text-blue-600" />
      : <ArrowDown className="w-3.5 h-3.5 text-blue-600" />;
  };
  
  // Render KPI skeleton
  const KPISkeleton = () => (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {[1, 2, 3].map((i) => (
        <Card key={i} className="border-slate-200">
          <CardContent className="p-4">
            <Skeleton className="h-4 w-24 mb-2" />
            <Skeleton className="h-8 w-16" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
  
  // Render table skeleton
  const TableSkeleton = () => (
    <div className="space-y-3">
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="flex items-center gap-4 p-3">
          <Skeleton className="h-10 w-10 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-48" />
            <Skeleton className="h-3 w-32" />
          </div>
          <Skeleton className="h-6 w-16" />
          <Skeleton className="h-4 w-24" />
        </div>
      ))}
    </div>
  );
  
  // Empty state component
  const EmptyState = () => (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mb-4">
        <Users className="w-10 h-10 text-slate-400" />
      </div>
      <h3 className="text-lg font-semibold text-slate-900 mb-2">
        {debouncedSearch || statusFilter !== "active" || contractFilter !== "all"
          ? `Aucun salarié trouvé${debouncedSearch ? ` pour "${debouncedSearch}"` : ''}`
          : "Aucun salarié enregistré"
        }
      </h3>
      <p className="text-sm text-slate-500 mb-6 max-w-md">
        {debouncedSearch || statusFilter !== "active" || contractFilter !== "all"
          ? "Essayez de modifier vos critères de recherche ou filtres."
          : "Commencez par ajouter votre premier salarié pour gérer vos ressources humaines."
        }
      </p>
      {!debouncedSearch && statusFilter === "active" && contractFilter === "all" ? (
        <Button 
          onClick={() => setShowCreateModal(true)}
          className="bg-blue-600 hover:bg-blue-700"
          data-testid="add-first-employee-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Ajouter le premier salarié
        </Button>
      ) : (
        <Button 
          onClick={clearFilters}
          variant="outline"
          data-testid="clear-filters-btn"
        >
          <X className="w-4 h-4 mr-2" />
          Effacer les filtres
        </Button>
      )}
    </div>
  );
  
  // Error state component
  const ErrorState = () => (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mb-4">
        <AlertCircle className="w-10 h-10 text-red-500" />
      </div>
      <h3 className="text-lg font-semibold text-slate-900 mb-2">
        Erreur de chargement
      </h3>
      <p className="text-sm text-slate-500 mb-6">
        {error}
      </p>
      <Button 
        onClick={fetchEmployees}
        variant="outline"
        data-testid="retry-btn"
      >
        <RefreshCw className="w-4 h-4 mr-2" />
        Réessayer
      </Button>
    </div>
  );

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="salaries-list-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground flex items-center gap-2">
            <Users className="w-8 h-8 text-blue-600" />
            Salariés
          </h1>
          <p className="text-muted-foreground mt-1">
            {loading ? "Chargement..." : `${total} salarié${total > 1 ? 's' : ''} enregistré${total > 1 ? 's' : ''}`}
          </p>
        </div>
        
        <Button 
          onClick={() => setShowCreateModal(true)}
          className="bg-blue-600 hover:bg-blue-700 shadow-sm"
          data-testid="add-employee-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Ajouter un salarié
        </Button>
      </div>
      
      {/* KPIs */}
      {loading && !stats ? (
        <KPISkeleton />
      ) : kpis && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Effectif total */}
          <Card className="border-slate-200 hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center shrink-0">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                    Effectif actif
                  </p>
                  <p className="text-2xl font-bold text-slate-900 tabular-nums">
                    {kpis.effectif}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Répartition contrats */}
          <Card className="border-slate-200 hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center shrink-0">
                  <Briefcase className="w-5 h-5 text-emerald-600" />
                </div>
                <div className="flex-1">
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                    Contrats
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs px-1.5">
                      {kpis.cdi} CDI
                    </Badge>
                    <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200 text-xs px-1.5">
                      {kpis.cdd} CDD
                    </Badge>
                    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-xs px-1.5">
                      {kpis.alternance} Alt.
                    </Badge>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Arrivées récentes */}
          <Card className="border-slate-200 hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center shrink-0">
                  <UserPlus className="w-5 h-5 text-violet-600" />
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                    Arrivées (30j)
                  </p>
                  <p className="text-2xl font-bold text-slate-900 tabular-nums">
                    {kpis.arrivals}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-3">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Rechercher par nom, prénom, matricule..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-white"
                data-testid="search-input"
              />
              {loading && debouncedSearch && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <RefreshCw className="w-4 h-4 text-slate-400 animate-spin" />
                </div>
              )}
            </div>
            
            {/* Status filter */}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger 
                className={`w-full md:w-[160px] bg-white ${statusFilter !== "active" ? 'border-blue-300 ring-1 ring-blue-100' : ''}`} 
                data-testid="status-filter"
              >
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="active">Actif</SelectItem>
                <SelectItem value="on_leave">En congé</SelectItem>
                <SelectItem value="terminated">Sorti</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Contract filter */}
            <Select value={contractFilter} onValueChange={setContractFilter}>
              <SelectTrigger 
                className={`w-full md:w-[160px] bg-white ${contractFilter !== "all" ? 'border-blue-300 ring-1 ring-blue-100' : ''}`} 
                data-testid="contract-filter"
              >
                <SelectValue placeholder="Type de contrat" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les contrats</SelectItem>
                <SelectItem value="cdi">CDI</SelectItem>
                <SelectItem value="cdd">CDD</SelectItem>
                <SelectItem value="alternance">Alternance</SelectItem>
                <SelectItem value="stage">Stage</SelectItem>
                <SelectItem value="interim">Intérim</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Clear filters button */}
            {activeFiltersCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-slate-500 hover:text-slate-700"
                data-testid="clear-filters-inline-btn"
              >
                <X className="w-4 h-4 mr-1" />
                Effacer ({activeFiltersCount})
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {error ? (
            <ErrorState />
          ) : loading && employees.length === 0 ? (
            <div className="p-4">
              <TableSkeleton />
            </div>
          ) : employees.length === 0 ? (
            <EmptyState />
          ) : (
            <>
              {/* Mobile Cards View */}
              <div className="block sm:hidden divide-y divide-slate-100">
                {employees.map((emp) => {
                  const personal = emp.personal_info || {};
                  const professional = emp.professional_info || {};
                  const contractLabel = getContractTypeLabel(professional.contract_type);
                  const contractColor = CONTRACT_BADGE_COLORS[professional.contract_type] || "bg-slate-100 text-slate-700";
                  const statusLabel = STATUS_LABELS[emp.status] || emp.status;
                  const statusColor = STATUS_BADGE_COLORS[emp.status] || "bg-slate-100 text-slate-600";
                  
                  return (
                    <div
                      key={emp.id}
                      className="p-4 active:bg-slate-50 cursor-pointer"
                      onClick={() => handleRowClick(emp.id)}
                      data-testid={`employee-card-${emp.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-semibold text-sm shrink-0">
                          {personal.first_name?.charAt(0)}{personal.last_name?.charAt(0)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <p className="font-medium text-slate-900 truncate">
                              {personal.last_name} {personal.first_name}
                            </p>
                            <ChevronRightIcon className="w-4 h-4 text-slate-400 shrink-0" />
                          </div>
                          <p className="text-sm text-slate-600 truncate">{professional.job_title || "-"}</p>
                          <div className="flex items-center gap-2 mt-2 flex-wrap">
                            <span className="font-mono text-xs text-slate-500">{emp.matricule}</span>
                            <Badge variant="outline" className={`${contractColor} text-[10px]`}>
                              {contractLabel}
                            </Badge>
                            <Badge variant="secondary" className={`${statusColor} text-[10px]`}>
                              {statusLabel}
                            </Badge>
                          </div>
                          <p className="text-xs text-slate-400 mt-1">
                            Embauche : {formatDateFr(professional.hire_date)}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Desktop Table View */}
              <div className="hidden sm:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50/50 hover:bg-slate-50/50">
                      <TableHead 
                        className="cursor-pointer select-none"
                        onClick={() => handleSort("last_name")}
                      >
                        <div className="flex items-center gap-1 text-xs font-semibold uppercase text-slate-600">
                          Nom
                          <SortIcon field="last_name" />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer select-none hidden md:table-cell"
                        onClick={() => handleSort("matricule")}
                      >
                        <div className="flex items-center gap-1 text-xs font-semibold uppercase text-slate-600">
                          Matricule
                          <SortIcon field="matricule" />
                        </div>
                      </TableHead>
                      <TableHead className="text-xs font-semibold uppercase text-slate-600">
                        Poste
                      </TableHead>
                      <TableHead className="text-xs font-semibold uppercase text-slate-600">
                        Contrat
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer select-none hidden lg:table-cell"
                        onClick={() => handleSort("hire_date")}
                      >
                        <div className="flex items-center gap-1 text-xs font-semibold uppercase text-slate-600">
                          Embauche
                          <SortIcon field="hire_date" />
                        </div>
                      </TableHead>
                      <TableHead className="text-xs font-semibold uppercase text-slate-600">
                        Statut
                      </TableHead>
                      <TableHead className="w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {employees.map((emp) => {
                      const personal = emp.personal_info || {};
                      const professional = emp.professional_info || {};
                      const contractLabel = getContractTypeLabel(professional.contract_type);
                      const contractColor = CONTRACT_BADGE_COLORS[professional.contract_type] || "bg-slate-100 text-slate-700";
                      const statusLabel = STATUS_LABELS[emp.status] || emp.status;
                      const statusColor = STATUS_BADGE_COLORS[emp.status] || "bg-slate-100 text-slate-600";
                      const categoryLabel = getEmployeeStatusLabel(professional.employee_status);
                      
                      return (
                        <TableRow 
                          key={emp.id}
                          className="cursor-pointer hover:bg-slate-50 transition-colors group"
                          onClick={() => handleRowClick(emp.id)}
                          data-testid={`employee-row-${emp.id}`}
                        >
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-semibold text-sm shrink-0">
                                {personal.first_name?.charAt(0)}{personal.last_name?.charAt(0)}
                              </div>
                              <div>
                                <p className="font-medium text-slate-900">
                                  {personal.last_name} {personal.first_name}
                                </p>
                                <p className="text-xs text-slate-500">
                                  {categoryLabel || "-"}
                                </p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="hidden md:table-cell">
                            <span className="font-mono text-sm text-slate-600">{emp.matricule}</span>
                          </TableCell>
                          <TableCell>
                            <p className="text-sm text-slate-700">{professional.job_title || "-"}</p>
                            <p className="text-xs text-slate-400">{professional.department || ""}</p>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={`${contractColor} text-xs`}>
                              {contractLabel}
                            </Badge>
                          </TableCell>
                          <TableCell className="hidden lg:table-cell">
                            <span className="text-sm text-slate-600 tabular-nums">
                              {formatDateFr(professional.hire_date)}
                            </span>
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary" className={`${statusColor} text-xs`}>
                              {statusLabel}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <ChevronRightIcon className="w-4 h-4 text-slate-300 group-hover:text-slate-500 transition-colors" />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex flex-col sm:flex-row items-center justify-between gap-2 px-4 py-3 border-t border-slate-200">
                  <p className="text-sm text-slate-500 text-center sm:text-left">
                    Page {page} sur {totalPages} ({total} résultat{total > 1 ? 's' : ''})
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      data-testid="prev-page-btn"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      data-testid="next-page-btn"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
      
      {/* Create Employee Modal */}
      <CreateEmployeeModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleEmployeeCreated}
      />
    </div>
  );
}
